'use strict';
const options_collection = {
  collectionsLoadMore: '.collections-load-more',
  rangeSlider: '.price-range .range-slider',
  rangeSliderMin: '.range-slider-min',
  rangeSliderMax: '.range-slider-max',
  minPriceRange: '.min-price-range',
  maxPriceRange: '.max-price-range',
  collectionSidebar: '#CollectionSidebar',
  collectionFilterBlocks: '.filter-blocks',
  productgridcontainer: '#productgridcontainer',
  collectionFiltersForm: '#CollectionFiltersForm',
  section: '.sec__collection-main',
};

// JS animations bypass the CSS reduced-motion kill-switch, so gate them here:
// with duration 0 the grid-mode switch still lands its end state instantly.
// (Unique name: top-level consts share the global scope with theme.js.)
const reduceMotionMediaCollection = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
);

let activeCollectionFilterTrigger = null;
const collectionDrawerFocusableSelector = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class BtnFilter extends HTMLElement {
  constructor() {
    super();
    this.collectionSidebar = document.getElementById('CollectionSidebar');
    this.onToggleBound = this.onToggle.bind(this);
    this.onKeydownBound = this.onKeydown.bind(this);
    this.onDocumentKeydownBound = this.onDocumentKeydown.bind(this);
    this.addEventListener('click', this.onToggleBound, false);
    this.addEventListener('keydown', this.onKeydownBound, false);
  }

  onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onToggle();
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusableElements = Array.from(
      this.collectionSidebar.querySelectorAll(collectionDrawerFocusableSelector)
    ).filter(
      (element) =>
        element.getAttribute('aria-hidden') !== 'true' &&
        element.getClientRects().length > 0
    );
    if (focusableElements.length === 0) {
      event.preventDefault();
      this.collectionSidebar.tabIndex = -1;
      this.collectionSidebar.focus();
      return;
    }
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements.at(-1);
    const focusIsInside = this.collectionSidebar.contains(
      document.activeElement
    );
    if (
      event.shiftKey &&
      (document.activeElement === firstFocusableElement || !focusIsInside)
    ) {
      event.preventDefault();
      lastFocusableElement.focus();
    } else if (
      !event.shiftKey &&
      (document.activeElement === lastFocusableElement || !focusIsInside)
    ) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }

  onToggle() {
    if (this.classList.contains('open')) {
      this.close();
      return;
    }
    this.open();
  }

  open() {
    if (!this.collectionSidebar) return;
    activeCollectionFilterTrigger?.close({ restoreFocus: false });
    activeCollectionFilterTrigger = this;
    this.collectionSidebar.classList.add('open');
    this.collectionSidebar.setAttribute('role', 'dialog');
    this.collectionSidebar.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add('open-drawer', 'open-sidebar');
    root.style.setProperty('padding-right', getScrollBarWidth.init() + 'px');
    this.classList.add('open');
    this.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onDocumentKeydownBound);
    requestAnimationFrame(() => {
      this.collectionSidebar.querySelector('close-filter')?.focus();
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.collectionSidebar) return;
    this.collectionSidebar.classList.remove('open');
    this.collectionSidebar.removeAttribute('role');
    this.collectionSidebar.removeAttribute('aria-modal');
    document.documentElement.classList.remove('open-drawer', 'open-sidebar');
    root.style.removeProperty('padding-right');
    this.classList.remove('open');
    this.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onDocumentKeydownBound);
    if (activeCollectionFilterTrigger === this) {
      activeCollectionFilterTrigger = null;
    }
    if (restoreFocus && this.isConnected) this.focus();
  }
}
customElements.define('btn-filter', BtnFilter);

class CloseFilter extends HTMLElement {
  constructor() {
    super();
    this.collectionSidebar = document.getElementById('CollectionSidebar');
    this.addEventListener('click', this.onClose.bind(this), false);
    this.addEventListener('keydown', this.onKeydown.bind(this), false);
  }

  onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.click();
  }

  onClose() {
    const trigger =
      activeCollectionFilterTrigger ||
      document.querySelector('btn-filter.open');
    if (trigger?.close) {
      trigger.close();
      return;
    }
    this.collectionSidebar.classList.remove('open');
    document.documentElement.classList.remove('open-drawer', 'open-sidebar');
    document.querySelectorAll('.btn-filter').forEach((button) => {
      button.classList.remove('open');
      button.setAttribute('aria-expanded', 'false');
    });
    root.style.removeProperty('padding-right');
  }
}
customElements.define('close-filter', CloseFilter);

class FacetApplyCanvas extends CloseFilter {
  constructor() {
    super();
    this.addEventListener('click', this.onClose.bind(this), false);
  }
}
customElements.define('facet-apply-canvas', FacetApplyCanvas);

class GridMode extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.gridMode.bind(this), false);
  }
  async gridMode() {
    if (this.classList.contains('active')) return;
    const view_mode = this.getAttribute('data-grid-mode');
    if (view_mode == 1) return;
  await Motion.animate(
      document.querySelectorAll(
        'motion-element.product-item__wrapper.slide_in'
      ),
      {
        transform: ['translateY(0)', 'translateY(3.5rem)'],
        opacity: [1, 0.01],
        visibility: ['visible', 'hidden'],
      },
      {
        duration: reduceMotionMediaCollection.matches ? 0 : 0.3,
        delay: reduceMotionMediaCollection.matches ? 0 : Motion.stagger(0.05),
        easing: [0, 0, 0.3, 1],
      }
    );


    for (var item of document.querySelectorAll('grid-mode')) {
      item.classList.remove('active');
    }
    const container_switch = document.querySelector(
      '.container-products-switch'
    );
    this.classList.add('active');
    const data_view_mode = container_switch.getAttribute('data-view-mode');
    container_switch.classList.remove('grid-columns-' + data_view_mode);
    container_switch.setAttribute('data-view-mode', view_mode);
    container_switch.classList.add('grid-columns-' + view_mode);
    Motion.animate(
      document.querySelectorAll(
        'motion-element.product-item__wrapper.slide_in'
      ),
      {
        transform: ['translateY(3.5rem)', 'translateY(0)'],
        opacity: [0.01, 1],
        visibility: ['hidden', 'visible'],
      },
      {
        duration: reduceMotionMediaCollection.matches ? 0 : 0.3,
        delay: reduceMotionMediaCollection.matches ? 0 : Motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      }
    );
  }
}
customElements.define('grid-mode', GridMode);

class ViewMode extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.viewMode.bind(this), false);
  }
  viewMode() {
    const view_mode = this.getAttribute('data-view');
    if (this.classList.contains('actived')) return;
    var queryString = window.location.search;
    queryString = eventCollectionShopify.removeParam('view', queryString);
    queryString = eventCollectionShopify.removeParam('columns', queryString);
    if (view_mode == 'list') {
      var searchParams = queryString.replace('?', '') + '&view=list';
    } else {
      var searchParams = queryString.replace('?', '') + '&view=grid';
      // The list-view column buttons are <view-mode> elements, so they carry the
      // column count they promise here; without it the grid returns at the
      // section default and the button lies about what it does.
      const columns = this.getAttribute('data-columns');
      if (columns) searchParams += '&columns=' + columns;
    }
    const url = eventCollectionShopify.renderUrl(searchParams);
    eventCollectionShopify.renderSectionFilter(url, searchParams);
  }
}
customElements.define('view-mode', ViewMode);

class FilterItem extends HTMLElement {
  constructor() {
    super();
    const disabled =
      this.classList.contains('option-disabled') ||
      this.closest('.disabled') !== null;
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (disabled) {
      this.setAttribute('aria-disabled', 'true');
      this.tabIndex = -1;
    } else if (!this.hasAttribute('tabindex')) {
      this.tabIndex = 0;
    }
    this.addEventListener('click', this.filterItem.bind(this), false);
    this.addEventListener('keydown', this.onKeydown.bind(this), false);
  }
  onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.click();
  }
  filterItem() {
    if (this.querySelector('.tooltip')) {
      this.querySelector('.tooltip').classList.toggle('current-filter');
    } else {
      if (this.closest('li')) {
        this.closest('li').classList.toggle('current-filter');
      }
    }
    const url = this.getAttribute('data-href');
    eventCollectionShopify.renderSectionFilter(url);
  }
}
customElements.define('filter-item', FilterItem);

class FacetClearAll extends CloseFilter {
  constructor() {
    super();
    this.addEventListener('click', this.filterItem.bind(this), false);
  }
  filterItem() {
    const url = this.getAttribute('data-href');
    eventCollectionShopify.renderSectionFilter(url);
    this.onClose();
  }
}
customElements.define('facet-clear-all', FacetClearAll);

class FilterSort extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.filterSort.bind(this), false);
    this.addEventListener('keydown', this.onKeydown.bind(this), false);
  }
  onKeydown(event) {
    if (
      ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
    ) {
      event.preventDefault();
      this.moveFocus(event.key);
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.click();
  }

  moveFocus(key) {
    const listbox = this.closest('[role="listbox"]');
    if (!listbox) return;
    const options = Array.from(
      listbox.querySelectorAll('filter-sort[role="option"]')
    );
    const currentIndex = options.indexOf(this);
    if (currentIndex === -1 || options.length === 0) return;
    let nextIndex = currentIndex;
    if (key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
    if (key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + options.length) % options.length;
    }
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = options.length - 1;
    const nextOption = options[nextIndex];
    for (const option of options) {
      option.tabIndex = option === nextOption ? 0 : -1;
    }
    nextOption.focus();
  }

  filterSort() {
    const value = this.getAttribute('value');
    const selectCustom = this.closest('.select-custom');
    selectCustom?.querySelectorAll('filter-sort').forEach((option) => {
      const isSelected = option === this;
      option.setAttribute('aria-selected', String(isSelected));
      option.tabIndex = isSelected ? 0 : -1;
      if (isSelected) {
        option.setAttribute('selected', 'selected');
      } else {
        option.removeAttribute('selected');
      }
    });
    const sorter = selectCustom?.querySelector('select-sorter');
    sorter?.updateSelectedValue(this.textContent.trim());
    sorter?.close();
    let queryString = window.location.search;
    queryString = eventCollectionShopify.removeParam('sort_by', queryString);
    const searchParams = queryString.replace('?', '') + '&sort_by=' + value;
    const url = eventCollectionShopify.renderUrl(searchParams);
    eventCollectionShopify.renderSectionFilter(url, searchParams);
  }
}
customElements.define('filter-sort', FilterSort);

class SelectSorter extends HTMLElement {
  constructor() {
    super();
    this.selectCustom = this.closest('.select-custom');
    this.sortList = document.getElementById(this.getAttribute('aria-controls'));
    this.onDocumentKeydownBound = this.onDocumentKeydown.bind(this);
    this.addEventListener('click', this.activeFilterSort.bind(this), false);
    this.addEventListener('keydown', this.onKeydown.bind(this), false);
    this.selectCustom
      ?.querySelector('.sort-close')
      ?.addEventListener('click', () => this.close());
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (
      event.key !== 'Enter' &&
      event.key !== ' ' &&
      event.key !== 'ArrowDown' &&
      event.key !== 'ArrowUp'
    ) {
      return;
    }
    event.preventDefault();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.open();
      return;
    }
    this.activeFilterSort();
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  activeFilterSort() {
    if (this.selectCustom.classList.contains('active')) {
      this.close();
      return;
    }
    this.open();
  }

  open() {
    this.selectCustom?.classList.add('active');
    this.setAttribute('aria-expanded', 'true');
    this.sortList?.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.onDocumentKeydownBound);
    requestAnimationFrame(() => {
      const selectedOption = this.sortList?.querySelector(
        'filter-sort[aria-selected="true"]'
      );
      const optionToFocus =
        selectedOption || this.sortList?.querySelector('filter-sort');
      if (!optionToFocus) return;
      for (const option of this.sortList.querySelectorAll('filter-sort')) {
        option.tabIndex = option === optionToFocus ? 0 : -1;
      }
      optionToFocus.focus();
    });
  }

  close() {
    this.selectCustom?.classList.remove('active');
    this.setAttribute('aria-expanded', 'false');
    this.sortList?.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.onDocumentKeydownBound);
    if (this.isConnected) this.focus();
  }

  updateSelectedValue(value) {
    const selectedValue = this.querySelector('.sort-selected-value');
    if (selectedValue) selectedValue.textContent = value;
  }
}
customElements.define('select-sorter', SelectSorter);

var eventCollectionShopify = (function () {
  return {
    collectionRequestId: 0,
    collectionRequestController: null,
    loadingTimeout: null,

    init: function () {
      this.ionRangeSlider();
      this.explainDisplayedPriceFilter();
      let windowWidth = window.innerWidth;
      if (windowWidth <= 1024) {
        const horizontal = document.querySelector('.horizontal-filter');
        if (horizontal) {
          const collapsible = document.querySelectorAll('collapsible-block');
          collapsible.forEach((e) => {
            e.classList.add('active');
          });
        }
      }
    },

    ionRangeSlider: function () {
      const price_range = document.querySelector('.price-range');
      const maxRange = document.querySelector(
        options_collection.rangeSliderMax
      );
      const minRange = document.querySelector(
        options_collection.rangeSliderMin
      );
      const minPrice = document.querySelector('.min-price-range');
      const maxPrice = document.querySelector('.max-price-range');
      var minPriceRange = 0;
      var maxPriceRange = 0;
      if (!price_range) return;
      document.querySelector(options_collection.minPriceRange).addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          const target = event.currentTarget;
          target.value = target.value.replace(',', '');
          if (Number(minRange.min) + Number(target.value) >= 0) {
            if (Number(target.value) > Number(maxRange.max)) {
              minPrice.value = Number(maxRange.max);
            } else {
              minPrice.value = Number(target.value);
            }
          } else {
            minPrice.value = Number(minRange.min);
          }
          const formData = new FormData(
            document.querySelector(options_collection.collectionFiltersForm)
          );
          const params = new URLSearchParams(formData).toString();
          const nameMin = minPrice.getAttribute('name');
          const nameMax = maxPrice.getAttribute('name');
          var queryString = window.location.search;
          queryString = this.removeParam(nameMin, queryString);
          queryString = this.removeParam(nameMax, queryString);
          var searchParams = queryString.replace('?', '') + '&' + params;
          const url = this.renderUrl(searchParams);
          this.renderSectionFilter(url, searchParams);
        },
        false
      );
      document.querySelector(options_collection.maxPriceRange).addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          const target = event.currentTarget;
          target.value = target.value.replace(',', '');
          if (Number(maxRange.max) - Number(target.value) >= 0) {
            maxPrice.value = Number(target.value);
          } else {
            maxPrice.value = Number(maxRange.max);
          }
          const formData = new FormData(
            document.querySelector(options_collection.collectionFiltersForm)
          );
          const params = new URLSearchParams(formData).toString();
          const nameMin = minPrice.getAttribute('name');
          const nameMax = maxPrice.getAttribute('name');
          var queryString = window.location.search;
          queryString = this.removeParam(nameMin, queryString);
          queryString = this.removeParam(nameMax, queryString);
          var searchParams = queryString.replace('?', '') + '&' + params;
          const url = this.renderUrl(searchParams);
          this.renderSectionFilter(url, searchParams);
        },
        false
      );
      document
        .querySelector(options_collection.rangeSliderMin)
        .addEventListener(
          'input',
          (event) => {
            event.preventDefault();
            const target = event.currentTarget;
            if (Number(maxRange.value) - Number(target.value) >= 1) {
              minPrice.value = Number(target.value);
              minPriceRange = Number(target.value) * 100;
            } else {
              target.value = Number(maxRange.value) - 1;
              minPriceRange = (Number(maxRange.value) - 1) * 100;
            }
            const price_format = Shopify.formatMoney(
              minPriceRange,
              cartStrings.money_format
            );
            document.querySelector('.price-lable .min').innerHTML =
              price_format;
            target
              .closest('.price-range')
              .style.setProperty(
                '--range-from',
                (minRange.value / Number(target.getAttribute('max'))) * 100 +
                  '%'
              );
          },
          false
        );
      document
        .querySelector(options_collection.rangeSliderMax)
        .addEventListener(
          'input',
          (event) => {
            event.preventDefault();
            const target = event.currentTarget;
            if (target.value - minRange.value >= 1) {
              maxPrice.value = target.value;
              maxPriceRange = Number(target.value) * 100;
            } else {
              target.value = Number(minRange.value) + Number(1);
              maxPriceRange = (Number(minRange.value) + Number(1)) * 100;
            }
            const price_format = Shopify.formatMoney(
              maxPriceRange,
              cartStrings.money_format
            );
            document.querySelector('.price-lable .max').innerHTML =
              price_format;
            target
              .closest('.price-range')
              .style.setProperty(
                '--range-to',
                (maxRange.value / Number(target.getAttribute('max'))) * 100 +
                  '%'
              );
          },
          false
        );
      document
        .querySelectorAll(options_collection.rangeSlider)
        .forEach((sliderRange) => {
          sliderRange.addEventListener(
            'change',
            (event) => {
              event.preventDefault();
              const target = event.currentTarget;
              const formData = new FormData(
                document.querySelector(options_collection.collectionFiltersForm)
              );
              const params = new URLSearchParams(formData).toString();
              const nameMin = minPrice.getAttribute('name');
              const nameMax = maxPrice.getAttribute('name');
              var queryString = window.location.search;
              queryString = this.removeParam(nameMin, queryString);
              queryString = this.removeParam(nameMax, queryString);
              var searchParams = queryString.replace('?', '') + '&' + params;
              const url = this.renderUrl(searchParams);
              this.renderSectionFilter(url, searchParams);
            },
            false
          );
        });
    },

    removeParam: function (key, sourceURL) {
      var rtn = sourceURL.split('?')[0],
        param,
        params_arr = [],
        queryString =
          sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : '';
      if (queryString !== '') {
        params_arr = queryString.split('&');
        for (var i = params_arr.length - 1; i >= 0; i -= 1) {
          param = params_arr[i].split('=')[0];
          if (param === key) {
            params_arr.splice(i, 1);
          }
        }
        if (params_arr.length) rtn = rtn + '?' + params_arr.join('&');
      }
      return rtn;
    },

    renderUrl: function (searchParams) {
      const sectionId = document.querySelector(options_collection.section)
        .dataset.sectionId;
      const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
      return url;
    },

    normalizeCollectionFocusLabel: function (value) {
      return value.replace(/\s+/g, ' ').trim();
    },

    captureCollectionFocus: function (section, activeElement) {
      if (
        !section ||
        !(activeElement instanceof HTMLElement) ||
        !section.contains(activeElement)
      ) {
        return null;
      }
      if (
        activeElement.closest('select-sorter, filter-sort, .select-custom')
      ) {
        return { kind: 'sorter' };
      }
      if (
        activeElement.closest(
          'btn-filter, close-filter, facet-clear-all, facet-apply-canvas'
        )
      ) {
        return { kind: 'filter-trigger' };
      }
      const filterItem = activeElement.closest('filter-item');
      if (filterItem) {
        const filterBlocks = filterItem.closest('.filter-blocks');
        const filterGroup = filterItem.closest('.filter-item');
        const groups = filterBlocks
          ? Array.from(filterBlocks.children).filter((child) =>
              child.classList.contains('filter-item')
            )
          : [];
        const options = filterGroup
          ? Array.from(filterGroup.querySelectorAll('ul.items filter-item'))
          : [];
        return {
          kind: 'filter-item',
          groupIndex: groups.indexOf(filterGroup),
          itemIndex: options.indexOf(filterItem),
          label: this.normalizeCollectionFocusLabel(filterItem.textContent),
        };
      }
      if (
        activeElement.matches('input[name], select[name], textarea[name]')
      ) {
        return {
          kind: 'named-control',
          tagName: activeElement.tagName,
          name: activeElement.getAttribute('name'),
        };
      }
      return null;
    },

    canRestoreCollectionFocus: function (element) {
      return Boolean(
        element instanceof HTMLElement &&
          element.isConnected &&
          !element.matches('[disabled]') &&
          element.getAttribute('aria-disabled') !== 'true' &&
          element.tabIndex >= 0 &&
          element.getClientRects().length > 0
      );
    },

    restoreCollectionFocus: function (section, focusState) {
      if (!section || !focusState) return;
      requestAnimationFrame(() => {
        let target = null;
        if (focusState.kind === 'sorter') {
          target = section.querySelector('select-sorter');
        } else if (focusState.kind === 'filter-trigger') {
          target = section.querySelector('btn-filter');
        } else if (focusState.kind === 'named-control') {
          target = Array.from(
            section.querySelectorAll('input[name], select[name], textarea[name]')
          ).find(
            (control) =>
              control.tagName === focusState.tagName &&
              control.getAttribute('name') === focusState.name
          );
        } else if (focusState.kind === 'filter-item') {
          const filterBlocks = section.querySelector('.filter-blocks');
          const groups = filterBlocks
            ? Array.from(filterBlocks.children).filter((child) =>
                child.classList.contains('filter-item')
              )
            : [];
          const filterGroup = groups[focusState.groupIndex];
          const options = filterGroup
            ? Array.from(filterGroup.querySelectorAll('ul.items filter-item'))
            : [];
          target = options.find(
            (option) =>
              this.normalizeCollectionFocusLabel(option.textContent) ===
              focusState.label
          );
          if (!this.canRestoreCollectionFocus(target)) {
            target = options[focusState.itemIndex];
          }
          if (!this.canRestoreCollectionFocus(target)) {
            target = options.find((option) =>
              this.canRestoreCollectionFocus(option)
            );
          }
        }
        if (!this.canRestoreCollectionFocus(target)) {
          target = section.querySelector('btn-filter');
        }
        if (this.canRestoreCollectionFocus(target)) {
          target.focus({ preventScroll: true });
          return;
        }
        if (!section.isConnected || section.getClientRects().length === 0) {
          return;
        }
        const hadTabIndex = section.hasAttribute('tabindex');
        if (!hadTabIndex) section.tabIndex = -1;
        section.focus({ preventScroll: true });
        if (!hadTabIndex) {
          section.addEventListener(
            'blur',
            () => section.removeAttribute('tabindex'),
            { once: true }
          );
        }
      });
    },

    renderSectionFilter: async function (url, searchParams) {
      const requestId = ++this.collectionRequestId;
      this.collectionRequestController?.abort();
      const requestController = new AbortController();
      this.collectionRequestController = requestController;
      const currentSection = document.querySelector(options_collection.section);
      const focusState = this.captureCollectionFocus(
        currentSection,
        document.activeElement
      );
      this.hideCollectionFilterFeedback(currentSection);
      this.toggleLoading(document.body, true);
      try {
        const response = await fetch(`${url}`, {
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const responseText = await response.text();
        if (requestId !== this.collectionRequestId) return;

        const responseDocument = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        let renderedSection = null;
        if (window.innerWidth <= 1024) {
          const collectionFilterBlocks = responseDocument.querySelector(
            options_collection.collectionFilterBlocks
          );
          const productgridcontainer = responseDocument.querySelector(
            options_collection.productgridcontainer
          );
          const currentFilterBlocks = document.querySelector(
            options_collection.collectionFilterBlocks
          );
          const currentProductGrid = document.querySelector(
            options_collection.productgridcontainer
          );
          if (
            !collectionFilterBlocks ||
            !productgridcontainer ||
            !currentFilterBlocks ||
            !currentProductGrid
          ) {
            throw new Error('Invalid collection filter response');
          }
          currentFilterBlocks.innerHTML = collectionFilterBlocks.innerHTML;
          currentProductGrid.innerHTML = productgridcontainer.innerHTML;
          renderedSection = document.querySelector(options_collection.section);
          const collectionSidebar = document.querySelector(
            options_collection.collectionSidebar
          );
          if (collectionSidebar?.classList.contains('open')) {
            const trigger =
              activeCollectionFilterTrigger ||
              document.querySelector('btn-filter.open');
            if (trigger?.close) {
              trigger.close();
            } else {
              collectionSidebar.classList.remove('open');
            }
          }
        } else {
          const newSection = responseDocument.querySelector(
            options_collection.section
          );
          const sectionToReplace = document.querySelector(
            options_collection.section
          );
          if (!newSection || !sectionToReplace) {
            throw new Error('Invalid collection filter response');
          }
          sectionToReplace.replaceWith(newSection);
          renderedSection = newSection;
        }

        document.documentElement.classList.remove(
          'hside_opened',
          'open-drawer',
          'open-sidebar'
        );
        this.updateUrl(url, searchParams);
        this.restoreCollectionFocus(renderedSection, focusState);

        const sectionElement = document.querySelector('#productgridcontainer');
        if (sectionElement) {
          const elementPosition = sectionElement.getBoundingClientRect().top;
          const offsetPosition = window.pageYOffset;
          const extraOffset = 100;
          const scrollToPosition =
            elementPosition + offsetPosition - extraOffset;
          window.scrollTo({
            top: scrollToPosition,
            behavior: reduceMotionMediaCollection.matches ? 'auto' : 'smooth',
          });
        }

        const motionCollection = document.querySelector('.motion-collection');
        motionCollection?.reloadAnimationEffect?.();
        window.initLazyloadItem?.();
        window.BlsLazyloadImg?.init?.();
        this.ionRangeSlider();
        this.explainDisplayedPriceFilter();
      } catch (error) {
        if (
          error.name === 'AbortError' ||
          requestId !== this.collectionRequestId
        ) {
          return;
        }
        console.error('Unable to update collection filters.', error);
        this.showCollectionFilterFeedback(
          document.querySelector(options_collection.section),
          url,
          searchParams,
          focusState
        );
      } finally {
        if (requestId !== this.collectionRequestId) return;
        if (this.collectionRequestController === requestController) {
          this.collectionRequestController = null;
        }
        this.toggleLoading(document.body, false);
      }
    },

    hideCollectionFilterFeedback: function (section) {
      const feedback = section?.querySelector(
        '[data-collection-filter-feedback]'
      );
      if (feedback) feedback.hidden = true;
    },

    showCollectionFilterFeedback: function (
      section,
      url,
      searchParams,
      focusState
    ) {
      const feedback = section?.querySelector(
        '[data-collection-filter-feedback]'
      );
      if (!feedback) return;
      feedback.hidden = false;
      const retry = feedback.querySelector('[data-collection-filter-retry]');
      if (retry) {
        retry.onclick = () => {
          feedback.hidden = true;
          this.renderSectionFilter(url, searchParams);
        };
      }
      this.restoreCollectionFocus(section, focusState);
    },

    formatCollectionMoney: function (cents) {
      if (window.Shopify?.formatMoney && window.cartStrings?.money_format) {
        return Shopify.formatMoney(cents, cartStrings.money_format);
      }
      const currency = window.Shopify?.currency?.active || 'EUR';
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    },

    // Shopify keeps a product when any variant price matches. Keep that result
    // and explain the lowest qualifying size when the card's normal "from"
    // price sits below the shopper's active range.
    explainDisplayedPriceFilter: function () {
      const params = new URLSearchParams(window.location.search);
      const gte = parseFloat(params.get('filter.v.price.gte'));
      const lte = parseFloat(params.get('filter.v.price.lte'));
      const hasGte = !isNaN(gte);
      const hasLte = !isNaN(lte);
      const cards = document.querySelectorAll(
        '#productgridcontainer .product-item.product__item-js'
      );
      cards.forEach((card) => {
        const existingContext = card.querySelector(
          '[data-price-filter-context]'
        );
        if (!hasGte && !hasLte) {
          existingContext?.remove();
          return;
        }

        let prices = [];
        try {
          prices = JSON.parse(card.dataset.variantPrices || '[]')
            .map((price) => Number.parseInt(price, 10))
            .filter((price) => Number.isFinite(price) && price > 0);
        } catch (error) {
          existingContext?.remove();
          return;
        }
        if (prices.length === 0) {
          existingContext?.remove();
          return;
        }

        const qualifyingPrices = prices.filter((price) => {
          const priceInCurrency = price / 100;
          return (
            (!hasGte || priceInCurrency >= gte) &&
            (!hasLte || priceInCurrency <= lte)
          );
        });
        const productMinimum = Math.min(...prices);
        const qualifyingMinimum = Math.min(...qualifyingPrices);
        if (
          qualifyingPrices.length === 0 ||
          qualifyingMinimum === productMinimum
        ) {
          existingContext?.remove();
          return;
        }

        const priceBox = card.querySelector('.card-product-price');
        if (!priceBox) return;
        const context = existingContext || document.createElement('small');
        context.className =
          'card-product-price__filter-context dark-grey w-full';
        context.dataset.priceFilterContext = '';
        const label = card.dataset.priceFilterLabel || '__PRICE__';
        context.textContent = label.replace(
          '__PRICE__',
          this.formatCollectionMoney(qualifyingMinimum)
        );
        if (!existingContext) priceBox.appendChild(context);
      });
    },

    toggleLoading: function (event, loading) {
      if (event) {
        window.clearTimeout(this.loadingTimeout);
        if (loading) {
          event.classList.remove('finish');
          event.classList.add('start', 'loading');
        } else {
          event.classList.add('finish');
          this.loadingTimeout = window.setTimeout(function () {
            event.classList.remove('start', 'loading', 'finish');
          }, 500);
        }
      }
    },

    updateUrl: function (url, searchParams = false) {
      if (searchParams) {
        const urlUpdate = `${window.location.pathname}?${searchParams}`;
        window.history.pushState({ url: urlUpdate }, '', urlUpdate);
      } else {
        window.history.pushState({ url: url }, '', url);
      }
    },
  };
})();
eventCollectionShopify.init();
